import { useRef } from 'react';
import styled from 'styled-components';
import { motion, useInView } from 'framer-motion';
import { FaWhatsapp, FaMapMarkerAlt, FaClock, FaEnvelope } from 'react-icons/fa';

const info = [
  {
    icon: <FaMapMarkerAlt size={20} />,
    title: 'Ubicaciones',
    lines: [
      'Matriz — Edo. de México',
      'Producción — San Juan de los Lagos - Tepatitlán de Morelos 12, 47135 Miti, Jal.',
    ],
  },
  {
    icon: <FaClock size={20} />,
    title: 'Horarios',
    lines: ['Lunes – Viernes: 8:00 am – 5:00 pm', 'Sábados: 8:00 am – 1:00 pm'],
  },
  {
    icon: <FaWhatsapp size={20} />,
    title: 'WhatsApp Ventas',
    lines: ['431 101 1283'],
    wa: true,
  },
];

function FadeUp({ children, delay = 0 }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-60px' });
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 35 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.65, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.div>
  );
}

export default function Contact() {
  return (
    <Section id="contact">
      <Inner>
        <FadeUp>
          <SectionTag>Contáctanos</SectionTag>
          <SectionTitle>Hablemos de tu <AccentWord>próximo pedido</AccentWord></SectionTitle>
          <SectionLead>
            Escríbenos por WhatsApp o correo electrónico y te responderemos
            a la brevedad con una cotización personalizada.
          </SectionLead>
        </FadeUp>

        <TwoCol>
          <InfoCol>
            {info.map((it, i) => (
              <FadeUp key={it.title} delay={i * 0.1}>
                <InfoCard>
                  <InfoIcon $wa={it.wa}>{it.icon}</InfoIcon>
                  <div>
                    <InfoTitle>{it.title}</InfoTitle>
                    {it.lines.map((l) => (
                      <InfoLine key={l}>
                        {it.wa ? (
                          <a href="https://wa.me/524311011283?text=Hola%2C%20los%20contacto%20desde%20su%20sitio%20web." target="_blank" rel="noreferrer">{l}</a>
                        ) : l}
                      </InfoLine>
                    ))}
                  </div>
                </InfoCard>
              </FadeUp>
            ))}

            <FadeUp delay={0.35}>
              <EmailCard>
                <FaEnvelope size={16} />
                <span>Escríbenos a: </span>
                <a href="mailto:jaoropezar@hotmail.com">jaoropezar@hotmail.com</a>
              </EmailCard>
            </FadeUp>
          </InfoCol>

          <FadeUp delay={0.15}>
            <MapBox>
              <MapLabel>
                <FaMapMarkerAlt size={13} />
                Planta de producción — Miti, Jalisco
              </MapLabel>
              <iframe
                title="Ubicación Grupo Jaycom"
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d1130.142126283191!2d-102.44350379182046!3d21.197122564916395!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x8429130022f61987%3A0x6215e0d55fd97ff7!2sTarimas%20Jaycom!5e0!3m2!1ses-419!2smx!4v1778958367954!5m2!1ses-419!2smx"
                width="100%"
                height="420"
                style={{ border: 0, display: 'block' }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </MapBox>
          </FadeUp>
        </TwoCol>
      </Inner>
    </Section>
  );
}

const Section = styled.section`
  background: transparent;
  padding: 7rem 2rem;
`;

const Inner = styled.div`
  max-width: 1280px;
  margin: 0 auto;
`;

const SectionTag = styled.p`
  font-size: 0.72rem;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  color: var(--blue);
  border-left: 3px solid var(--red);
  padding-left: 0.75rem;
  margin-bottom: 1rem;
`;

const SectionTitle = styled.h2`
  font-size: clamp(2rem, 5vw, 3.2rem);
  margin-bottom: 1.25rem;
  color: var(--blue);
`;

const AccentWord = styled.span`
  color: var(--red);
  text-shadow: var(--shadow-3d);
`;

const SectionLead = styled.p`
  max-width: 560px;
  font-size: 0.95rem;
  line-height: 1.8;
  color: var(--text-sub);
  margin-bottom: 4rem;
`;

const TwoCol = styled.div`
  display: grid;
  grid-template-columns: 1fr 1.3fr;
  gap: 2rem;
  align-items: start;
  @media (max-width: 900px) { grid-template-columns: 1fr; }
`;

const InfoCol = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const InfoCard = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 1.25rem;
  padding: 1.4rem 0;
  transition: padding-left 0.2s;
  &:hover { padding-left: 0.5rem; }
`;

const InfoIcon = styled.div`
  flex-shrink: 0;
  color: ${({ $wa }) => $wa ? 'var(--green-wa)' : 'var(--red)'};
  padding-top: 0.15rem;
`;

const InfoTitle = styled.p`
  font-size: 0.7rem;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: var(--text-sub);
  margin-bottom: 0.3rem;
`;

const InfoLine = styled.p`
  font-size: 0.9rem;
  color: var(--text);
  line-height: 1.5;
  a { color: var(--green-wa); font-weight: 600; }
`;

const EmailCard = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.4rem;
  padding: 1.1rem 0;
  font-size: 0.85rem;
  color: var(--text-sub);
  svg { color: var(--red); }
  a { color: var(--red); font-weight: 600; }
`;

const MapBox = styled.div`
  iframe { border-radius: 4px; }
`;

const MapLabel = styled.p`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.78rem;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--text-sub);
  margin-bottom: 0.75rem;
  svg { color: var(--red); flex-shrink: 0; }
`;
